import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Check, Eye, EyeOff, Loader2, MoveLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Input } from 'components/ui/input';
import { Button, buttonVariants } from 'components/ui/button';
import { PATH_ROUTES } from 'routes/path';
import { PhoneInput } from 'components/ui/phone-input';
import { Separator } from 'components/ui/separator';
import useAuthStore from 'store/auth';
import {
  OTPRegisterFormData,
  OTPRegisterValidator,
} from 'lib/validators/otp-register';
import { useSignUpOTP } from 'services/auth/hook';
import { encryptText } from 'lib/kms';
import useAuthenticate from 'hooks/useAuthenticate';
import { Label } from 'components/ui/label';

export default function OTPRegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  console.log('🚀 ~ OTPRegisterPage ~ state:', state);

  const { onAuthenticate } = useAuthenticate();

  const phoneRef = useRef<HTMLInputElement>(null);

  const setSignupEmail = useAuthStore((store) => store.setSignupEmail);
  const setSignupPhone = useAuthStore((store) => store.setSignupPhone);
  const setSignUpUserId = useAuthStore((store) => store.setSignupUserId);
  const resetSignUp = useAuthStore((store) => store.resetSignUp);
  const setSignupPassword = useAuthStore((store) => store.setSignupPassword);
  const setEmailVerified = useAuthStore((store) => store.setEmailVerified);
  const setPhoneVerified = useAuthStore((store) => store.setPhoneVerified);

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [phone, setPhone] = useState<string>(state.phoneNumber || '');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    resetSignUp();
  }, []);

  const { mutate } = useSignUpOTP({
    onSuccess: (res, { username, password, phone_number }) => {
      if (!res?.phone_verification_flag || !res?.email_verification_flag) {
        setSignupEmail(username);
        setSignupPassword(password!);
        setSignupPhone(phone_number!);
        setSignUpUserId(res?.user_id || '');
        setEmailVerified(res?.email_verification_flag || false);
        setPhoneVerified(res?.phone_verification_flag || false);
        setIsLoading(false);
        navigate(PATH_ROUTES.SIGNUP_VERIFICATION);
        return;
      }

      onAuthenticate({
        email: username,
        password: password!,
        setLoading: setIsLoading,
      });
    },
    onError: () => {
      setIsLoading(false);
    },
  });

  const form = useForm<OTPRegisterFormData>({
    resolver: zodResolver(OTPRegisterValidator),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    if (state?.email) {
      form.reset({
        email: state.email || '',
        password: '',
        confirmPassword: '',
      });
    }
  }, [state]);

  async function onSubmit(values: OTPRegisterFormData) {
    setIsLoading(true);
    const { email, password } = values;

    try {
      const encryptedPassword = await encryptText(password);
      mutate({
        username: email,
        password: encryptedPassword,
        phone_number: phone,
        ...(state?.phoneNumber && { session: state.session || '' }),
      });
    } catch (error) {
      toast.error(t('failed_account_create_msg'));
      setIsLoading(false);
    }
  }

  return (
    <>
      <div className='absolute hidden lg:block top-4 left-4'>
        <Link
          to={PATH_ROUTES.LOGIN}
          className={buttonVariants({
            variant: 'ghost',
            className: 'rounded-md',
          })}
        >
          <MoveLeft className='w-5 h-5' />
        </Link>
      </div>

      <div className='flex flex-col items-center w-full space-y-8'>
        <div className='w-4/5 mx-auto sm:w-2/5'>
          <h2 className='text-2xl font-semibold leading-none tracking-tight text-center transition-colors text-primary'>
            {t('share_your_details')}
          </h2>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className='w-full px-8 space-y-8 sm:w-2/3'
          >
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('email')}</FormLabel>

                  <FormControl>
                    <div className='relative'>
                      <Input {...field} disabled={!!state.email} />
                      {state?.email ? (
                        <div className='absolute top-2 right-2'>
                          <div className='grid rounded-full size-5 place-content-center bg-primary/70'>
                            <span className='sr-only'>Email verified</span>
                            <Check className='text-white size-3' />
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div>
              <Label>{t('phone')}</Label>
              <div className='relative'>
                <PhoneInput
                  // @ts-expect-error correctly passed ref
                  ref={phoneRef}
                  id='phone'
                  defaultCountry='MX'
                  value={phone}
                  onChange={setPhone}
                  disabled={!!state.phoneNumber}
                />
                {state?.phoneNumber ? (
                  <div className='absolute top-2 right-2'>
                    <div className='grid rounded-full size-5 place-content-center bg-primary/70'>
                      <span className='sr-only'>Phone verified</span>
                      <Check className='text-white size-3' />
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <FormField
              control={form.control}
              name='password'
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor='password'>{t('password')}</FormLabel>
                  <FormControl>
                    <div className='relative'>
                      <Input
                        className='pr-10'
                        {...field}
                        type={showPassword ? 'text' : 'password'}
                        id='password'
                      />
                      <Button
                        type='button'
                        variant='ghost'
                        className='absolute right-0 rounded-md -top-1'
                        onClick={() => setShowPassword((prev) => !prev)}
                        data-testid='toggle-password'
                      >
                        {showPassword ? (
                          <Eye className='w-5 h-5 text-primary' />
                        ) : (
                          <EyeOff className='w-5 h-5 text-primary' />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='confirmPassword'
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor='confirm-password'>
                    {t('confirm_password')}
                  </FormLabel>
                  <FormControl>
                    <div className='relative'>
                      <Input
                        className='pr-10'
                        {...field}
                        type={showConfirmPassword ? 'text' : 'password'}
                        id='confirm-password'
                      />
                      <Button
                        type='button'
                        variant='ghost'
                        className='absolute right-0 rounded-md -top-1'
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        data-testid='toggle-confirm-password'
                      >
                        {showConfirmPassword ? (
                          <Eye className='w-5 h-5 text-primary' />
                        ) : (
                          <EyeOff className='w-5 h-5 text-primary' />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className='flex justify-center !mt-10'>
              <Button
                type='submit'
                className='px-10 font-semibold rounded-full'
                disabled={
                  !form.formState.isValid ||
                  isLoading ||
                  (!state.phoneNumber && !phone)
                }
              >
                {isLoading && (
                  <Loader2
                    role='progressbar'
                    className='w-4 h-4 mr-2 animate-spin'
                  />
                )}{' '}
                {t('sign_up')}
              </Button>
            </div>
          </form>
        </Form>

        <div className='flex flex-col items-center justify-center'>
          <div className='flex items-center gap-x-2'>
            <a
              href={process.env.REACT_APP_MEDSI_TOC}
              target='_blank'
              rel='noopener noreferrer'
              className={buttonVariants({
                variant: 'link',
                className: 'cursor-pointer',
              })}
            >
              {t('tnc')}
            </a>
            <Separator orientation='vertical' className='h-4' />
            <a
              href={process.env.REACT_APP_MEDSI_PRIVACY}
              target='_blank'
              rel='noopener noreferrer'
              className={buttonVariants({
                variant: 'link',
                className: 'cursor-pointer',
              })}
            >
              {t('pp')}
            </a>
          </div>

          <div>
            <span className='font-medium'>{t('have_acount')}</span>{' '}
            <Link
              to={PATH_ROUTES.LOGIN}
              className={buttonVariants({ variant: 'link' })}
            >
              {t('sign_in')}
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
